import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from 'Components/UI/Breadcrumb';
import Editor from 'Components/Form/Editor/Editor';
import { useNavigate } from 'react-router-dom';
import { createPage } from 'helpers/API Calls/System & Settings/Pages';
import placeholderImage from "assets/images/img.png"
import Dropzone from 'Components/Form/DropZone';
import Hint from 'Components/UI/Hint';

interface PageFormValues {
  title: string;
  sub_title: string;
  image_caption: string;
  content: string;
  is_seo: boolean;
  meta_title: string;
  meta_keywords: string;
  meta_description: string;
  is_featured: boolean;
}

function AddPage({type_id, link, title}: {type_id: number, link: string, title: string}) {

  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  const formik = useFormik<PageFormValues>({    
    initialValues: {
      title: '',
      sub_title: '',
      image_caption: '',
      content: '',
      is_seo: false,
      meta_title: '',
      meta_keywords: '',
      meta_description: '',
      is_featured: false,
    },
    validationSchema: yup.object().shape({
      title: yup.string().required('Title is required'),
      sub_title: yup.string().required('Sub title is required'),
      image_caption: yup.string(),      
      meta_title: yup.string().when('is_seo', { 
        is: true, 
        then: schema => schema.required('Meta title is required when SEO is enabled')
      }),
      meta_keywords: yup.string().when('is_seo', { 
        is: true, 
        then: schema => schema.required('Meta keywords are required when SEO is enabled')
      }),
      meta_description: yup.string().when('is_seo', {
        is: true, 
        then: schema => schema.required('Meta description is required when SEO is enabled')
      }),
    }),    
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const pageData = {
        title: values.title,
        sub_title: values.sub_title,
        image_caption: values.image_caption,
        content,
        is_seo: values.is_seo ? 1 : 0,
        meta_title: values.meta_title,
        meta_keywords: values.meta_keywords,
        meta_description: values.meta_description,
        page_type_id: type_id,
        is_featured: values.is_featured ? 1 : 0,
        status: 1,
      };
      const response = await createPage(pageData, selectedImage);
      if (response.success) {
        navigate(link);
      }
      setSubmitting(false);
    },
  });
  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: title, link }, {label: 'Create'}]} />
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Basic Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Basic Information</CardTitle>
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="title">Title <span className="text-danger">*</span></Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        placeholder="Enter page title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.title && formik.errors.title ? true : false}
                      />
                      {formik.errors.title && formik.touched.title && (
                        <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="sub_title">Sub Title <span className="text-danger">*</span></Label>
                      <Input
                        id="sub_title"
                        name="sub_title"
                        type="text"
                        placeholder="Enter page sub title"
                        value={formik.values.sub_title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.sub_title && formik.errors.sub_title ? true : false}
                      />
                      {formik.errors.sub_title && formik.touched.sub_title && (
                        <FormFeedback type="invalid">{formik.errors.sub_title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                    <Label htmlFor="image_caption">Image Caption</Label>
                    <Input
                        id="image_caption"
                        name="image_caption"
                        type="text"
                        placeholder="Enter image caption"
                        value={formik.values.image_caption}
                        onChange={formik.handleChange}
                        invalid={formik.touched.image_caption && formik.errors.image_caption ? true : false}
                    />
                    {formik.errors.image_caption && formik.touched.image_caption && (
                        <FormFeedback type="invalid">{formik.errors.image_caption}</FormFeedback>
                    )}
                    </div>
                </Col>
                </Row>
                <Row>
                {/* logo */}
                <Col md="6">
                    <div className="mb-3" id="featured-image-section">
                        <Label htmlFor="name-input">Featured Image</Label>
                        <Dropzone
                        accept = {{"image/*": [".png", ".jpeg", ".jpg"]}}
                        onDrop = {(acceptedFiles) => {
                            setSelectedImage(acceptedFiles[0]);
                        }}
                        onDelete={() => {setSelectedImage(null);}}
                        showDelete={!!selectedImage}
                        renderContent = {() => (
                            <div className="text-center cursor-pointer">
                            <div className="position-relative d-inline-block" >
                                <div className={`avatar-xl rounded`}>
                                <div className="avatar-title bg-light ">
                                    <img
                                    src={selectedImage ? URL.createObjectURL(selectedImage) : placeholderImage}
                                    id="logo-img"
                                    alt=""
                                    className="avatar-xl rounded"
                                    />
                                </div>
                                </div>
                            </div>
                            </div>
                        )}
                        />
                    </div>
                </Col>
              
                </Row>
              </div>

              {/* Content */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Page Content</CardTitle>
                <div className="mb-3">
                  <Editor content={content} setContent={setContent} />
                </div>
              </div>

              {/* SEO Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>SEO Information</CardTitle>
                <div className="mb-3">
                  <div className="form-check mb-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_seo"
                      name="is_seo"
                      checked={formik.values.is_seo}
                      onChange={formik.handleChange}
                    />
                    <Label className="form-check-label" htmlFor="is_seo">
                      Index this page in search engines <Hint type="index" />
                    </Label>
                  </div>
                </div>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="meta_title">
                        Meta Title {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="title" />
                      </Label>
                      <Input
                        id="meta_title"
                        name="meta_title"
                        type="text"
                        placeholder="Enter meta title"
                        value={formik.values.meta_title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_title && formik.errors.meta_title ? true : false}
                      />
                      {formik.errors.meta_title && formik.touched.meta_title && (
                        <FormFeedback type="invalid">{formik.errors.meta_title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="meta_keywords">
                        Meta Keywords {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="keywords" />
                      </Label>
                      <Input
                        id="meta_keywords"
                        name="meta_keywords"
                        type="text"
                        placeholder="Enter meta keywords"
                        value={formik.values.meta_keywords}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_keywords && formik.errors.meta_keywords ? true : false}
                      />
                      {formik.errors.meta_keywords && formik.touched.meta_keywords && (
                        <FormFeedback type="invalid">{formik.errors.meta_keywords}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="12">
                    <div className="mb-3">
                      <Label htmlFor="meta_description">
                        Meta Description {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="description" />
                      </Label>
                      <Input
                        id="meta_description"
                        name="meta_description"
                        type="textarea"
                        rows="3"
                        placeholder="Enter meta description"
                        value={formik.values.meta_description}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_description && formik.errors.meta_description ? true : false}
                      />
                      {formik.errors.meta_description && formik.touched.meta_description && (
                        <FormFeedback type="invalid">{formik.errors.meta_description}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Page Settings */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Page Settings</CardTitle>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_featured"
                          name="is_featured"
                          checked={formik.values.is_featured}
                          onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="is_featured">
                          Featured Page
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="d-flex mb-3 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Creating...' : 'Create Page'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export default AddPage;
